// ─────────────────────────────────────────────────────────────────────────────
// V2Bills — /dashboard/bills (Bills & Expenses) in the v2 design.
// Endpoints are v1's: GET /api/bills?category&status&month&year, POST/PUT (multipart
// with optional attachment), PATCH /:id/mark-paid, DELETE /:id, GET /api/purchases
// (normalised into the list exactly as v1 does), /uploads/bills/:file for attachments.
// GST preview uses v1's determineGSTType + calcGST.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Segmented, Sheet, Skel, Switch, inr } from "./primitives";
import { Field, PageHead, Stat } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const CATS = ["Rent & Utilities", "Salaries & Staff", "Raw Material / Purchases", "Travel & Transport", "Office Supplies", "Marketing & Ads", "Repairs & Maintenance", "Taxes & Government Fees", "Other"];
const CAT_ICON = { "Rent & Utilities": "building", "Salaries & Staff": "people", "Raw Material / Purchases": "box", "Travel & Transport": "truck", "Office Supplies": "note", "Marketing & Ads": "sparkle", "Repairs & Maintenance": "pulse", "Taxes & Government Fees": "gst", Other: "wallet" };
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "Credit Card", "Other"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── v1 GST logic ──
const stateCode = (g) => (g?.length >= 2 ? g.substring(0, 2).toUpperCase() : null);
const gstTypeOf = (buyer, vendor) => { const b = stateCode(buyer), v = stateCode(vendor); if (!b || !v) return null; return b === v ? "CGST_SGST" : "IGST"; };
const calcGST = (base, rate, type) => {
  if (!rate) return { cgst: 0, sgst: 0, igst: 0, totalGST: 0, total: base };
  const totalGST = parseFloat(((base * rate) / 100).toFixed(2));
  if (type === "IGST") return { cgst: 0, sgst: 0, igst: totalGST, totalGST, total: parseFloat((base + totalGST).toFixed(2)) };
  const half = parseFloat((totalGST / 2).toFixed(2));
  return { cgst: half, sgst: totalGST - half, igst: 0, totalGST, total: parseFloat((base + totalGST).toFixed(2)) };
};
const isOverdue = (b) => b.paymentStatus === "unpaid" && b.dueDate && new Date(b.dueDate) < new Date();

export default function V2Bills({ showAlert }) {
  usePageTitle("Bills & Expenses");
  const navigate = useNavigate();
  const { user, hasPermission } = useContext(userContext);
  const canWrite = hasPermission("bills", "write");
  const now = new Date();
  const [bills, setBills] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [cat, setCat] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [removing, setRemoving] = useState(null);
  const newBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cat) params.set("category", cat); if (status) params.set("status", status); if (month) params.set("month", month); if (year) params.set("year", year);
    try {
      const [b, p] = await Promise.all([
        fetch(`${API_BASE}/api/bills?${params}`, { headers: auth() }).then((r) => r.json()).catch(() => []),
        fetch(`${API_BASE}/api/purchases`, { headers: auth() }).then((r) => r.json()).catch(() => []),
      ]);
      setBills(Array.isArray(b) ? b : []);
      setPurchases((Array.isArray(p) ? p : []).map((x) => ({
        _id: x._id, _source: "purchase", title: `${x.purchaseNumber}${x.supplierId?.supplierName ? " · " + x.supplierId.supplierName : ""}`,
        category: x.expenseCategory || "Raw Material / Purchases", baseAmount: x.subtotal, amount: x.grandTotal, billDate: x.purchaseDate, dueDate: x.dueDate,
        vendor: x.supplierId?.supplierName || "", vendorGSTIN: x.supplierId?.supplierGSTIN || "",
        totalGST: (x.totalCgst || 0) + (x.totalSgst || 0) + (x.totalIgst || 0), gstRate: 0, isGSTEligible: true,
        paymentStatus: x.status === "PAID" ? "paid" : "unpaid", description: x.notes || "",
      })).filter((x) => {
        if (!x.billDate) return true;
        const d = new Date(x.billDate);
        if (month && d.getMonth() + 1 !== parseInt(month)) return false;
        if (year && d.getFullYear() !== parseInt(year)) return false;
        return true;
      }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cat, status, month, year]); // eslint-disable-line

  const all = useMemo(() => [...bills, ...purchases.filter((p) => (!cat || p.category === cat) && (!status || p.paymentStatus === status))]
    .sort((a, b) => new Date(b.billDate) - new Date(a.billDate)), [bills, purchases, cat, status]);
  const shown = all.filter((b) => { const t = q.trim().toLowerCase(); return !t || `${b.title} ${b.vendor || ""}`.toLowerCase().includes(t); });
  const total = all.reduce((s, b) => s + (b.amount || 0), 0);
  const unpaid = all.filter((b) => b.paymentStatus === "unpaid").reduce((s, b) => s + (b.amount || 0), 0);
  const overdueN = all.filter(isOverdue).length;
  const itc = all.filter((b) => b.isGSTEligible && b.totalGST > 0).reduce((s, b) => s + b.totalGST, 0);
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  const menu = (b) => b._source === "purchase"
    ? [{ label: "Open in purchase bills", glyph: "arrowUpRight", onSelect: () => navigate("/dashboard/purchase-entry") }]
    : [
      ...(canWrite && b.paymentStatus === "unpaid" ? [{ label: "Mark paid", glyph: "check", onSelect: () => setPaying(b) }] : []),
      ...(canWrite ? [{ label: "Edit", glyph: "note", onSelect: () => setEditing(b) }] : []),
      ...(b.attachment?.filename ? [{ label: "View attachment", glyph: "arrowUpRight", onSelect: () => window.open(`${API_BASE}/uploads/bills/${b.attachment.filename}`, "_blank") }] : []),
      ...(canWrite ? ["divider", { label: "Delete", glyph: "close", danger: true, onSelect: () => setRemoving(b) }] : []),
    ];

  return (
    <div className="v2p">
      <PageHead eyebrow="Payables" title="Bills & expenses" sub="Rent, salaries, utilities and every other cost — with GST you can claim back."
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setEditing("new")}><Glyph name="plus" size={17} stroke={2.2} /> Add expense</button>} />
      <div className="v2k-stats">
        <Stat label="Total spend" value={loading ? "—" : inr(total)} note={`${all.length} entries`} />
        <Stat label="Unpaid" value={loading ? "—" : inr(unpaid)} note={overdueN ? `${overdueN} overdue` : "Nothing overdue"} tone={overdueN ? "warn" : undefined} />
        <Stat label="Paid" value={loading ? "—" : inr(total - unpaid)} note="Settled" />
        <Stat label="Input GST (ITC)" value={loading ? "—" : inr(itc)} note="On eligible bills" />
      </div>
      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={status} onChange={setStatus} options={[{ value: "", label: "All" }, { value: "unpaid", label: "Unpaid" }, { value: "paid", label: "Paid" }]} />
        <select className="v2-input v2b-sel" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month"><option value="">All months</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
        <select className="v2-input v2b-sel" value={year} onChange={(e) => setYear(e.target.value)} aria-label="Year"><option value="">All years</option>{years.map((y) => <option key={y}>{y}</option>)}</select>
        <div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title or vendor" aria-label="Search bills" /></div>
      </div>
      <div className="v2b-cats" role="tablist" aria-label="Category">
        <button type="button" className={`v2-chip-btn v2i-preset${!cat ? " is-on" : ""}`} onClick={() => setCat("")}>All categories</button>
        {CATS.map((c) => <button key={c} type="button" className={`v2-chip-btn v2i-preset${cat === c ? " is-on" : ""}`} onClick={() => setCat(cat === c ? "" : c)}>{c}</button>)}
      </div>
      <section className="v2-card v2i-card">
        {loading && !all.length ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={58} r={16} />)}</div>
          : shown.length === 0 ? <div className="v2c-empty"><Duo name="wallet" size={48} /><strong>No expenses here</strong><span>Add rent, salaries, travel or anything else you pay for — attach the receipt while you're at it.</span></div>
          : <div className="v2i-rows">{shown.map((b) => (
            <div key={`${b._source || "b"}${b._id}`} className="v2cn-row">
              <button type="button" className="v2cn-open" onClick={() => (b._source === "purchase" ? navigate("/dashboard/purchase-entry") : canWrite && setEditing(b))}>
                <Duo name={CAT_ICON[b.category] || "wallet"} size={30} />
                <span className="v2-row-main">
                  <span className="v2i-no">{b.title}{b._source === "purchase" && <span className="v2cn-link">Purchase bill</span>}{b.attachment?.filename && <Glyph name="note" size={13} />}</span>
                  <span className="v2-row-sub">{b.category} · {b.vendor || "No vendor"} · {fmtDate(b.billDate)}{b.dueDate && b.paymentStatus === "unpaid" ? ` · due ${fmtDate(b.dueDate)}` : ""}</span>
                </span>
                <span className="v2cn-end">
                  <span className="v2i-total">{inr(b.amount, { decimals: 2 })}</span>
                  {b.paymentStatus === "paid" ? <span className="v2-status v2-status--good"><Glyph name="check" size={13} stroke={2.2} /> Paid</span>
                    : isOverdue(b) ? <span className="v2-status v2-status--bad"><Glyph name="alert" size={13} stroke={2.2} /> Overdue</span>
                    : <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> Unpaid</span>}
                </span>
              </button>
              <MenuButton items={menu(b)} className="v2-icon-btn v2i-more" label={`Actions for ${b.title}`} width={230} />
            </div>))}</div>}
      </section>

      <BillEditor value={editing} originRef={newBtn} companyGSTIN={user?.companyGSTIN} onClose={() => setEditing(null)}
        onSaved={(msg) => { setEditing(null); showAlert?.(msg, "success"); load(); }} />
      <Sheet open={!!paying} onClose={() => setPaying(null)} width={420} title="Mark as paid" subtitle={paying ? `${paying.title} · ${inr(paying.amount, { decimals: 2 })}` : ""}
        footer={<><button type="button" className="v2-btn" onClick={() => setPaying(null)}>Cancel</button>
          <button type="button" className="v2-btn v2-btn--primary" onClick={async () => {
            try {
              const r = await fetch(`${API_BASE}/api/bills/${paying._id}/mark-paid`, { method: "PATCH", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify({ paymentMode: paying.mode || "Cash" }) });
              const d = await r.json().catch(() => ({}));
              if (!r.ok) throw new Error(d.error || "Couldn't update");
              showAlert?.("Marked as paid", "success"); setPaying(null); load();
            } catch (e) { setPaying((p) => ({ ...p, error: e.message })); }
          }}>Mark paid</button></>}>
        <Field label="Paid via"><select className="v2-input" value={paying?.mode || "Cash"} onChange={(e) => setPaying((p) => ({ ...p, mode: e.target.value }))}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></Field>
        {paying?.error && <div className="v2-banner v2-banner--bad" style={{ marginTop: 12 }}><Glyph name="alert" size={16} /> {paying.error}</div>}
      </Sheet>
      <Sheet open={!!removing} onClose={() => setRemoving(null)} width={420} title="Delete this expense?"
        footer={<><button type="button" className="v2-btn" onClick={() => setRemoving(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={async () => {
            try {
              const r = await fetch(`${API_BASE}/api/bills/${removing._id}`, { method: "DELETE", headers: auth() });
              const d = await r.json().catch(() => ({}));
              if (!r.ok) throw new Error(d.error || "Couldn't delete");
              showAlert?.("Expense deleted", "success"); setRemoving(null); load();
            } catch (e) { setRemoving((x) => ({ ...x, error: e.message })); }
          }}>Delete</button></>}>
        <p className="v2c-remove-text">{removing?.title} ({inr(removing?.amount, { decimals: 2 })}) and its attachment will be removed permanently.</p>
        {removing?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {removing.error}</div>}
      </Sheet>
    </div>
  );
}

const EMPTY = () => ({ title: "", category: CATS[0], amount: "", billDate: new Date().toISOString().split("T")[0], dueDate: "", vendor: "", vendorGSTIN: "", gstRate: "0", isGSTEligible: "true", description: "", paymentStatus: "unpaid", paymentMode: "", attachment: null });

function BillEditor({ value, originRef, companyGSTIN, onClose, onSaved }) {
  const edit = value && value !== "new" ? value : null;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  useEffect(() => {
    if (!value) return;
    setErr("");
    setF(edit ? {
      title: edit.title, category: edit.category, amount: edit.baseAmount || edit.amount, billDate: edit.billDate?.split("T")[0] || "",
      dueDate: edit.dueDate?.split("T")[0] || "", vendor: edit.vendor || "", vendorGSTIN: edit.vendorGSTIN || "", gstRate: String(edit.gstRate ?? "0"),
      isGSTEligible: String(edit.isGSTEligible ?? "true"), description: edit.description || "", paymentStatus: edit.paymentStatus, paymentMode: edit.paymentMode || "", attachment: null,
    } : EMPTY());
  }, [value]); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const base = parseFloat(f.amount) || 0, rate = parseFloat(f.gstRate) || 0;
  const type = gstTypeOf(companyGSTIN, f.vendorGSTIN);
  const g = calcGST(base, rate, type);

  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.title || !f.category || !f.amount || !f.billDate) { setErr("Title, category, amount and bill date are required."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (k === "attachment") { if (v) fd.append("attachment", v); } else if (v !== "" && v !== null) fd.append(k, v); });
      const r = await fetch(edit ? `${API_BASE}/api/bills/${edit._id}` : `${API_BASE}/api/bills`, { method: edit ? "PUT" : "POST", headers: auth(), body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save");
      onSaved(edit ? "Expense updated" : "Expense added");
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!value} onClose={onClose} originRef={edit ? undefined : originRef} width={640} title={edit ? "Edit expense" : "Add expense"}
      footer={<><span className="v2cn-foot-total">Total <b>{inr(g.total, { decimals: 2 })}</b></span>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2b-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : edit ? "Save changes" : "Add expense"}</button></>}>
      <form id="v2b-form" className="v2-form" onSubmit={save}>
        <Field label="What was it for?"><input className="v2-input" autoFocus value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Office rent — November" /></Field>
        <Field label="Category">
          <div className="v2b-catgrid">{CATS.map((c) => (
            <button key={c} type="button" className={`v2b-cat${f.category === c ? " is-on" : ""}`} onClick={() => set("category", c)}>
              <Duo name={CAT_ICON[c]} size={24} /><span>{c}</span>
            </button>))}</div>
        </Field>
        <div className="v2-form-row">
          <Field label="Amount (before GST)">
            <span className="v2-input-wrap"><span className="v2-input-prefix" style={{ fontSize: 16 }}>₹</span>
              <input className="v2-input" style={{ paddingLeft: 30 }} type="number" step="0.01" min="0" value={f.amount} onChange={(e) => set("amount", e.target.value)} /></span>
          </Field>
          <Field label="GST rate"><Segmented size="sm" ariaLabel="GST rate" value={String(f.gstRate)} onChange={(v) => set("gstRate", v)} options={["0", "5", "12", "18", "28"].map((r) => ({ value: r, label: `${r}%` }))} /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Vendor" optional><input className="v2-input" value={f.vendor} onChange={(e) => set("vendor", e.target.value)} /></Field>
          <Field label="Vendor GSTIN" optional hint={rate ? (type ? (type === "IGST" ? "Inter-state → IGST" : "Same state → CGST + SGST") : "Add GSTINs to split CGST/SGST vs IGST") : undefined}>
            <input className="v2-input v2-mono" value={f.vendorGSTIN} onChange={(e) => set("vendorGSTIN", e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 15))} /></Field>
        </div>
        {rate > 0 && base > 0 && (
          <div className="v2-inset v2cn-totals">
            {type === "IGST" ? <div className="v2-inset-row v2-inset-row--static"><span>IGST {rate}%</span><b>{inr(g.igst, { decimals: 2 })}</b></div>
              : <><div className="v2-inset-row v2-inset-row--static"><span>CGST {rate / 2}%</span><b>{inr(g.cgst, { decimals: 2 })}</b></div>
                <div className="v2-inset-row v2-inset-row--static"><span>SGST {rate / 2}%</span><b>{inr(g.sgst, { decimals: 2 })}</b></div></>}
            <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>Total</span><b>{inr(g.total, { decimals: 2 })}</b></div>
          </div>
        )}
        <div className="v2-inset"><Switch id="v2b-itc" checked={f.isGSTEligible === "true"} onChange={(v) => set("isGSTEligible", v ? "true" : "false")} label="Claim GST as input tax credit" description="Counts toward ITC in your GST figures" /></div>
        <div className="v2-form-row">
          <Field label="Bill date"><input className="v2-input" type="date" value={f.billDate} onChange={(e) => set("billDate", e.target.value)} /></Field>
          <Field label="Due date" optional><input className="v2-input" type="date" value={f.dueDate} onChange={(e) => set("dueDate", e.target.value)} /></Field>
        </div>
        <div className="v2-form-row">
          <Field label="Status"><Segmented ariaLabel="Payment status" value={f.paymentStatus} onChange={(v) => setF((p) => ({ ...p, paymentStatus: v, paymentMode: v === "paid" ? (p.paymentMode || "Cash") : p.paymentMode }))} options={[{ value: "unpaid", label: "Unpaid" }, { value: "paid", label: "Paid" }]} /></Field>
          {f.paymentStatus === "paid" && <Field label="Paid via"><select className="v2-input" value={f.paymentMode || "Cash"} onChange={(e) => set("paymentMode", e.target.value)}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></Field>}
        </div>
        <Field label="Notes" optional><input className="v2-input" value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="Receipt" optional hint="PDF or image">
          <button type="button" className="v2b-drop" onClick={() => fileRef.current?.click()}>
            <Glyph name="upload" size={18} /> {f.attachment ? f.attachment.name : edit?.attachment?.filename ? "Replace attached file" : "Attach a file"}
          </button>
          <input ref={fileRef} type="file" accept="image/*,.pdf" hidden onChange={(e) => set("attachment", e.target.files?.[0] || null)} />
        </Field>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
